import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { baseUrl } from 'App';
import { useModal } from './ModalContext';
import { saveData } from 'services/api/saveData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faRotateRight, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import IconButton from 'components/buttons/IconButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import '../../style/scss/modals.scss';

// Type definition of the photo shown in the modal
export interface PhotoViewType {
    id: number;
    file: string;
    thumbnail?: string | null;
    filename: string;
    description?: string | null;
    created?: string | Date;
    created_by_user_full_name?: string | null;
}

interface PhotoViewModalProps {
    viewKey?: string;
    photo: PhotoViewType;
    photos?: PhotoViewType[];
    editable?: boolean;
    onSaved?: (photo: PhotoViewType) => void;
    onDeleted?: (photoId: number) => void;
}

const PhotoViewModal: React.FC<PhotoViewModalProps> = ({
    photo,
    photos,
    editable = true,
    onSaved,
    onDeleted,
}) => {
    const { t } = useTranslation();
    const { revealModal, closeModal } = useModal();
    const [currentPhoto, setCurrentPhoto] = useState(photo);
    const [description, setDescription] = useState(photo.description || '');
    const [rotation, setRotation] = useState(0);
    const [saveLoader, setSaveLoader] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);

    // Reveal the modal on component mount
    useEffect(() => revealModal(), []);

    // Determine the position of the current photo in the photo list
    const photoIndex = photos ? photos.findIndex(listPhoto => listPhoto.id === currentPhoto.id) : -1;
    const hasPreviousPhoto = photoIndex > 0;
    const hasNextPhoto = photoIndex !== -1 && photos !== undefined && photoIndex < photos.length - 1;
    const showNavigation = photos !== undefined && photos.length > 1 && photoIndex !== -1;

    // Navigate to the previous or next photo in the list
    const handleNavigate = (direction: number) => {
        // Get the photo to navigate to and skip when it does not exist
        const newPhoto = photos?.[photoIndex + direction];
        if (!newPhoto) return;

        // Show the new photo and reset the description and rotation
        setCurrentPhoto(newPhoto);
        setDescription(newPhoto.description || '');
        setRotation(0);
    };

    // Get the file url of the photo
    const getFileUrl = (file?: string | null) => {
        let fileUrl = '';

        if (file) {
            if (file.startsWith('http') || process.env.NODE_ENV === 'production') {
                fileUrl = file;
            } else {
                fileUrl = `${baseUrl}${file}`;
            }
        }

        return fileUrl;
    };

    // Rotate the photo preview a quarter turn clockwise
    const handleRotate = () => {
        setRotation((currentRotation) => (currentRotation + 90) % 360);
    };

    // Save the description and the rotation of the photo
    const handleSave = async () => {
        try {
            setSaveLoader(true);

            // Keep track of the updated photo values
            let updatedPhoto: PhotoViewType = { ...currentPhoto, description };

            // Save the description when it changed
            if (description !== (currentPhoto.description || '')) {
                const response: any = await saveData({
                    apiUrl: `patch_photo/${currentPhoto.id}`,
                    method: 'patch',
                    data: { description },
                });

                if (response?.data) {
                    updatedPhoto = { ...updatedPhoto, ...response.data };
                }
            }

            // Rotate the photo file when the preview was rotated
            if (rotation % 360 !== 0) {
                const response: any = await saveData({
                    apiUrl: `post_photo_rotate/${currentPhoto.id}`,
                    method: 'post',
                    data: { degrees: rotation % 360 },
                });

                if (response?.data) {
                    updatedPhoto = { ...updatedPhoto, ...response.data };
                }
            }

            // Call the on saved callback and close the modal
            if (onSaved) onSaved(updatedPhoto);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setSaveLoader(false);
        }
    };

    // Soft delete the photo and close the modal
    const handleDelete = async () => {
        try {
            setDeleteLoader(true);

            await saveData({
                apiUrl: `patch_photo/${currentPhoto.id}`,
                method: 'patch',
                data: { deleted: true },
            });

            // Call the on deleted callback and close the modal
            if (onDeleted) onDeleted(currentPhoto.id);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setDeleteLoader(false);
        }
    };

    return (
        <div className='photo-view-modal'>
            <h4 className='header text-center'>{currentPhoto.filename}</h4>

            <div className='photo-view-body'>
                {showNavigation && (
                    <button
                        type='button'
                        className='photo-view-nav-button'
                        onClick={() => handleNavigate(-1)}
                        disabled={!hasPreviousPhoto}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                )}
                <div className={`photo-view-image-container ${rotation % 180 !== 0 ? 'quarter-rotated' : ''}`}>
                    <img
                        src={getFileUrl(currentPhoto.file)}
                        alt={currentPhoto.filename}
                        className='photo-view-image'
                        style={{ transform: rotation ? `rotate(${rotation}deg)` : undefined }}
                    />
                </div>
                {showNavigation && (
                    <button
                        type='button'
                        className='photo-view-nav-button'
                        onClick={() => handleNavigate(1)}
                        disabled={!hasNextPhoto}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                )}
            </div>

            {editable ? (
                <textarea
                    className='photo-view-description-input'
                    rows={3}
                    value={description}
                    placeholder={t('job.photos.description_placeholder')}
                    onChange={(event) => setDescription(event.target.value)}
                />
            ) : (
                currentPhoto.description && (
                    <p className='photo-view-description-text'>{currentPhoto.description}</p>
                )
            )}

            {editable && (
                <div className='button-row'>
                    <div className='buttons-left'>
                        <IconButton
                            type='button'
                            onClick={handleDelete}
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            loading={deleteLoader}
                            tooltipText='job.photos.delete_photo_tooltip'
                            size='small'
                        />
                        <IconButton
                            type='button'
                            onClick={handleRotate}
                            icon={<FontAwesomeIcon icon={faRotateRight} />}
                            tooltipText='job.photos.rotate_photo_tooltip'
                            size='small'
                        />
                    </div>
                    <div className='buttons-right'>
                        <SecondaryButton
                            onClick={closeModal}
                            label='general.cancel'
                            size='small'
                        />
                        <PrimaryButton
                            type='button'
                            onClick={handleSave}
                            label='general.save'
                            size='small'
                            loading={saveLoader}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhotoViewModal;
